import React, { useCallback } from 'react';
import { ArrowIcon, RegularButton, RegularButtonSize } from 'fronton-react';
import { cx } from 'linaria';
import { QtyCounter } from 'common/components/qty-counter';
import { getProductId, isWebSellable } from 'features/product-info/ducks/selectors';
import {
  getPrimaryPriceUnitName,
  getIsPricesSwapped,
  getSecondPrice,
  SecondPrice,
} from 'features/product-info';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { useTranslation } from 'react-i18next';
import {
  BasketItemFlow,
  getMeasurementSettings,
  ViewsModels,
  BasketFlowActions,
  getProductQuantityAccordingToSelectedServices,
} from 'features/basket';
import { ConnectedProps } from 'types/utils';
import { getGoodsSupplyCase } from 'features/goods-supply/selectors';
import { GoodsSupplyCase } from 'features/goods-supply/types';
import { RootReducer } from 'store/root-reducer';
import { addToBasketSticky, newButtonStyle } from 'common/components/product-price-view/sticky.styles';
import { analyticsUtils, patchUrlByLocale } from 'utils';
import { getIsBot } from 'common/selectors';
import { counterWrapper, toBasket } from './styles';

export const DATA_QA_PRODUCT_CART_BUTTON = 'product-cart-button';

export const DATA_QA_TO_BASKET_BUTTON = 'to-basket-button';

export type AddToBasketMappedState = {
  productId: string;
  quantity: number;
  supplyCase: GoodsSupplyCase | null;
  isSellable: boolean;
  measurementSettings: ViewsModels.MeasurementSettings | null;
  mainUom: string;
  isSwapPrice: boolean;
  secondPrice?: SecondPrice;
  size?: RegularButtonSize;
  isStickyPanel?: boolean;
  isBot: boolean;
};

export type AddToBasketProps = ConnectedProps<AddToBasketMappedState, typeof mapDispatchToProps> &
  React.HTMLAttributes<HTMLDivElement>;

export const AddToBasket: React.FC<AddToBasketProps> = ({
  onBasketChange,
  onBasketRemove,
  className = '',
  classnameCounter = '',
  productId,
  quantity,
  supplyCase,
  isSellable,
  measurementSettings,
  mainUom,
  isSwapPrice,
  secondPrice,
  size = 'l',
  isStickyPanel,
  isBot,
  ...htmlProps
}) => {
  const { t } = useTranslation();
  const toBasketClickHandler = useCallback(() => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementBlock: 'productDetail',
      elementName: 'toBasket',
      elementText: 'Перейти в корзину',
      elementType: 'button',
    });
  }, []);
  const uom = isSwapPrice ? secondPrice?.unit : mainUom;
  const changeHandler = useCallback(
    (value: number) => {
      const flowQty = value - quantity;
      if (flowQty !== 0) {
        onBasketChange(productId, flowQty);
      }
    },
    [productId, onBasketChange, quantity],
  );

  const stickyPanelStyle = isStickyPanel ? addToBasketSticky : '';
  const basketNewStyle = cx(stickyPanelStyle, newButtonStyle);

  const handleAddToCart = () => {
    const step = measurementSettings.step || 1;
    let initialSteps = Math.round(1 / step);
    if (step * initialSteps !== 1) {
      initialSteps += 1;
    }
    if (step > 1) {
      initialSteps = 1;
    }

    onBasketChange(productId, step * initialSteps);
  };

  let content: React.ReactNode = null;

  if (isSellable) {
    const inBasket = Boolean(quantity);

    content = (measurementSettings || isBot) && (
      <div className={className} {...htmlProps}>
        {inBasket ? (
          <div className={counterWrapper}>
            <QtyCounter
              step={measurementSettings?.step}
              min={measurementSettings?.step}
              overriddenValue={quantity}
              className={classnameCounter}
              isStickyPanel={isStickyPanel}
              uom={uom}
              onChange={(val: number) => changeHandler(val)}
              onRemove={() => onBasketRemove(productId)}
            />
            {!isStickyPanel && (
              <RegularButton
                className={isStickyPanel ? cx(toBasket, addToBasketSticky) : toBasket}
                iconOnly
                data-qa={DATA_QA_TO_BASKET_BUTTON}
                size={size}
                label={t('product-info.add-to-basket.aria-label.go-to-cart')}
                onClick={toBasketClickHandler}
                href={patchUrlByLocale('/basket/')}
              >
                <ArrowIcon direction="right" />
              </RegularButton>
            )}
          </div>
        ) : (
          <RegularButton
            className={basketNewStyle}
            data-qa={DATA_QA_PRODUCT_CART_BUTTON}
            onClick={handleAddToCart}
            aria-label={t('product-info.add-to-basket.aria-label.add-to-cart')}
            size={size}
            wide
          >
            {t('product-info.add-to-basket.button.add-to-cart')}
          </RegularButton>
        )}
      </div>
    );

    if (supplyCase !== null && ['CASE_1', 'CASE_2', 'CASE_5'].includes(supplyCase)) {
      content = null;
    }
  }

  return <>{content}</>;
};
const mapDispatchToProps = (dispatch: Dispatch) => ({
  onBasketChange: (productId: BasketItemFlow['productId'], flowQty: BasketItemFlow['flowQty']) =>
    dispatch(BasketFlowActions.UPDATE_ITEM({ productId, flowQty })),
  onBasketRemove: (productId: string) => dispatch(BasketFlowActions.RESET_ITEM({ productId })),
});

const mapStoreToProps = (store: RootReducer, props: AddToBasketProps): AddToBasketMappedState => {
  const productId = getProductId(store) || '';
  const quantity = getProductQuantityAccordingToSelectedServices(productId)(store);
  const supplyCase = getGoodsSupplyCase(store);
  const isSellable = isWebSellable(store);
  const measurementSettings = getMeasurementSettings(store);
  const mainUom = props.uom ?? getPrimaryPriceUnitName(store) ?? '';
  const isSwapPrice = props.isSwapPrice ?? getIsPricesSwapped(store) ?? false;
  const secondPrice = getSecondPrice(store) as SecondPrice;
  const isBot = getIsBot(store);

  return {
    productId,
    quantity,
    supplyCase,
    isSellable,
    measurementSettings,
    mainUom,
    isSwapPrice,
    secondPrice,
    isBot,
  };
};

export const PdpAddToBasket = connect(mapStoreToProps, mapDispatchToProps)(AddToBasket);
